import { useState, useEffect, useCallback, useRef } from 'react';

interface SessionTimeoutInfo {
  timeout_seconds: number;
  remaining_seconds: number;
  last_activity: string;
}

interface UseSessionTimeoutOptions {
  enabled?: boolean;
  onTimeout?: () => void;
  warningThresholdSeconds?: number;
  extendCooldownMs?: number;
  checkIntervalSeconds?: number;
}

interface UseSessionTimeoutReturn {
  remainingSeconds: number;
  timeoutSeconds: number;
  isWarningShown: boolean;
  extendSession: (force?: boolean) => Promise<boolean>;
  hideWarning: () => void;
}

/**
 * Custom hook to manage session timeout with automatic extension
 * and warning modal when session is about to expire.
 * 
 * This hook:
 * - Fetches session timeout info from the server
 * - Periodically extends the session to keep it alive
 * - Shows a warning when approaching timeout
 * - Provides countdown of remaining time
 */
export const useSessionTimeout = ({
  enabled = true,
  onTimeout,
  warningThresholdSeconds = 60,
  extendCooldownMs = 5000,
  checkIntervalSeconds = 30,
}: UseSessionTimeoutOptions = {}): UseSessionTimeoutReturn => {
  const [remainingSeconds, setRemainingSeconds] = useState<number>(600); // Default 10 minutes
  const [timeoutSeconds, setTimeoutSeconds] = useState<number>(600);
  const [isWarningShown, setIsWarningShown] = useState<boolean>(false);
  
  const lastExtendTimeRef = useRef<number>(0);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());
  // Track if session was recently extended to prevent warning from re-appearing immediately
  const sessionExtendedRef = useRef<boolean>(false);
  // Guard against concurrent extend calls (e.g. trackActivity mid-flight when user clicks)
  const extendInFlightRef = useRef<boolean>(false);
  // PHA-712: ref mirror of isWarningShown so trackActivity can read current value
  // without adding isWarningShown to its dependency array (which would re-bind all
  // activity listeners on every warning state change and introduce stale closures).
  const isWarningShownRef = useRef<boolean>(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5802';

  /**
   * Fetch current session timeout information from server
   */
  const fetchTimeoutInfo = useCallback(async () => {
    if (!enabled) return;

    try {
      const response = await fetch(`${apiBaseUrl}/session/timeout_info`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data: SessionTimeoutInfo = await response.json();
        setTimeoutSeconds(data.timeout_seconds);
        setRemainingSeconds(data.remaining_seconds);
        console.log(`Session timeout: ${data.remaining_seconds}s remaining of ${data.timeout_seconds}s total`);
      } else {
        console.warn('Failed to fetch timeout info:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch timeout info:', error);
    }
  }, [enabled, apiBaseUrl]);

  /**
   * Extend the session by calling the backend endpoint.
   * @param force - If true, bypass the cooldown period (used when user clicks "Stay Logged In")
   * @returns true on successful extend, false on failure or when skipped (disabled/cooldown)
   */
  const extendSession = useCallback(async (force: boolean = false): Promise<boolean> => {
    if (!enabled) return false;

    const now = Date.now();

    // Only extend if cooldown period has passed (unless forced)
    if (!force && now - lastExtendTimeRef.current < extendCooldownMs) {
      console.log('Session extend on cooldown, skipping...');
      return false;
    }

    // Prevent concurrent background extends from piling up (e.g. trackActivity
    // bursts while a fetch is in flight). Only non-forced calls are blocked —
    // forced calls (user clicking "Stay Logged In") always proceed to the server
    // so they get a real success/failure signal and never trigger a spurious
    // logout on a live session due to a collision with a background extend.
    if (!force && extendInFlightRef.current) return false;
    if (!force) extendInFlightRef.current = true;

    try {
      const response = await fetch(`${apiBaseUrl}/session/extend`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Session extended:', data.last_activity);
        lastExtendTimeRef.current = now;

        // Mark that session was extended - prevents warning from re-appearing immediately
        sessionExtendedRef.current = true;

        // Hide warning immediately
        setIsWarningShown(false);

        // Fetch updated timeout info from server
        await fetchTimeoutInfo();

        // Clear the extended flag after a brief delay to allow state to settle
        setTimeout(() => {
          sessionExtendedRef.current = false;
        }, 2000);

        return true;
      } else {
        console.warn('Failed to extend session:', response.status);
        return false;
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
      return false;
    } finally {
      if (!force) extendInFlightRef.current = false;
    }
  }, [enabled, apiBaseUrl, extendCooldownMs, fetchTimeoutInfo]);

  /**
   * Track user activity and extend session when appropriate.
   *
   * PHA-712: once the idle-warning modal is shown, ambient mouse/keyboard activity
   * must NOT auto-extend/dismiss it — the user must explicitly click
   * "Stay Logged In" / "Logout Now" or let the 10s auto-lock fire.
   * This decouples idle-timer activity from an already-open warning.
   * The ref (not state) is read here to avoid adding isWarningShown to the
   * dependency array, which would re-bind all activity listeners and create
   * stale closures on every warning state change.
   */
  const trackActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
    // PHA-712: if the warning modal is already visible, do not silently extend
    // the session — the user must take an explicit action.
    if (isWarningShownRef.current) return;
    // Extend session on activity (with cooldown)
    extendSession();
  }, [extendSession]);

  /**
   * Hide the warning modal
   */
  const hideWarning = useCallback(() => {
    setIsWarningShown(false);
  }, []);

  /**
   * Handle session timeout
   */
  const handleTimeout = useCallback(() => {
    console.log('Session timeout reached');
    
    // Clear intervals
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    // Call the timeout callback if provided
    if (onTimeout) {
      onTimeout();
    }
  }, [onTimeout]);

  /**
   * Update countdown every second
   */
  const updateCountdown = useCallback(() => {
    setRemainingSeconds((prev) => {
      const newRemaining = Math.max(0, prev - 1);
      
      // Show warning when approaching timeout, but only if:
      // 1. We're below the threshold
      // 2. We have time remaining
      // 3. Warning isn't already shown
      // 4. Session wasn't recently extended (prevents immediate re-show)
      if (newRemaining <= warningThresholdSeconds && 
          newRemaining > 0 && 
          !sessionExtendedRef.current) {
        setIsWarningShown(true);
      }
      
      // Handle timeout
      if (newRemaining <= 0) {
        handleTimeout();
      }
      
      return newRemaining;
    });
  }, [warningThresholdSeconds, handleTimeout]);

  // PHA-712: keep the ref in sync with state so trackActivity can read it without
  // adding isWarningShown to its dependency array.
  useEffect(() => {
    isWarningShownRef.current = isWarningShown;
  }, [isWarningShown]);

  /**
   * Setup activity tracking
   */
  useEffect(() => {
    if (!enabled) return;

    const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    
    // Throttle activity tracking to avoid excessive API calls
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledTrackActivity = () => {
      if (!throttleTimeout) {
        trackActivity();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, extendCooldownMs);
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, throttledTrackActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledTrackActivity);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [enabled, trackActivity, extendCooldownMs]);

  /**
   * Initialize and start periodic checks
   */
  useEffect(() => {
    if (!enabled) {
      // Clean up if disabled
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      return;
    }

    // Fetch initial timeout info
    fetchTimeoutInfo();

    // Update countdown every second
    countdownIntervalRef.current = setInterval(updateCountdown, 1000);

    // Check timeout info periodically
    checkIntervalRef.current = setInterval(() => {
      fetchTimeoutInfo();
    }, checkIntervalSeconds * 1000);

    // Cleanup
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [enabled, fetchTimeoutInfo, updateCountdown, checkIntervalSeconds]);

  return {
    remainingSeconds,
    timeoutSeconds,
    isWarningShown,
    extendSession,
    hideWarning,
  };
};
